/**
 * Template Customization Editor
 *
 * Main component for managing template customizations in a project.
 * Displays list of customizations and provides CRUD operations.
 */

import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Code, AlertCircle, Info } from 'lucide-react';
import type { TemplateCustomization } from '@onecreator/shared';
import { TemplateCustomizationForm } from './TemplateCustomizationForm';
import { api } from '../../api';

interface TemplateCustomizationEditorProps {
  projectId: string;
  catalogVersion: number;
  readOnly?: boolean;
}

interface TemplateCustomizationMap {
  [templateId: string]: TemplateCustomization;
}

interface TemplateInfo {
  id: string;
  label: string;
  slots: string[];
  recommended?: string[];
}

export function TemplateCustomizationEditor({
  projectId,
  catalogVersion: _catalogVersion,
  readOnly = false,
}: TemplateCustomizationEditorProps) {
  const [customizations, setCustomizations] = useState<TemplateCustomizationMap>({});
  const [availableTemplates, setAvailableTemplates] = useState<TemplateInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch customizations and available templates
  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch customizations and available templates in parallel
      const [customizationsRes, templatesRes] = await Promise.all([
        api.get(`/projects/${projectId}/template-customizations`),
        api.get(`/projects/${projectId}/template-customizations-meta/available-templates`),
      ]);

      setCustomizations(customizationsRes.data.data || {});
      setAvailableTemplates(templatesRes.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch customizations:', err);
      setError(err.response?.data?.message || 'Failed to load customizations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTemplateId(null);
    setIsFormOpen(true);
  };

  const handleEdit = (templateId: string) => {
    setEditingTemplateId(templateId);
    setIsFormOpen(true);
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm(`Eliminare la customizzazione per il template "${templateId}"?`)) {
      return;
    }

    try {
      await api.delete(`/projects/${projectId}/template-customizations/${templateId}`);
      await fetchData();
    } catch (err: any) {
      alert(`Errore durante l'eliminazione: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleSave = async (templateId: string, customization: TemplateCustomization) => {
    try {
      await api.put(
        `/projects/${projectId}/template-customizations/${templateId}`,
        customization
      );
      await fetchData();
      setIsFormOpen(false);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Failed to save customization');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Caricamento customizzazioni...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg m-6">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Errore</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const customizedTemplateIds = Object.keys(customizations);
  const uncustomizedTemplates = availableTemplates.filter(
    (t) => !customizedTemplateIds.includes(t.id)
  );

  return (
    <div className="template-customization-editor h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Template Customization
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Personalizza i template del catalogo per questo progetto (
              {customizedTemplateIds.length}/{availableTemplates.length} customizzati)
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleAdd}
              disabled={uncustomizedTemplates.length === 0}
              className="btn-primary flex items-center space-x-2"
              title={
                uncustomizedTemplates.length === 0
                  ? 'Tutti i template sono già customizzati'
                  : 'Aggiungi nuova customizzazione'
              }
            >
              <Plus className="w-4 h-4" />
              <span>Nuova Customizzazione</span>
            </button>
          )}
        </div>

        {/* Info Banner */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-blue-800">
            <p>
              Le customizzazioni estendono i template del catalogo senza modificarli.
              Puoi aggiungere wrapper HTML, classi CSS custom ed elementi decorativi.
              Le modifiche sono specifiche per questo progetto.
            </p>
          </div>
        </div>
      </div>

      {/* Customizations List */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {customizedTemplateIds.length === 0 ? (
          <div className="text-center py-12">
            <Code className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">Nessuna customizzazione presente</p>
            <p className="text-sm text-gray-400 mb-4">
              Inizia customizzando un template dal catalogo
            </p>
            {!readOnly && uncustomizedTemplates.length > 0 && (
              <button
                onClick={handleAdd}
                className="btn-secondary inline-flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Crea la prima customizzazione</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {customizedTemplateIds.map((templateId) => {
              const customization = customizations[templateId];
              const templateInfo = availableTemplates.find((t) => t.id === templateId);

              return (
                <div
                  key={templateId}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 hover:shadow-md transition-all duration-200"
                >
                  {/* Card Header */}
                  <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {templateInfo?.label || templateId}
                        </h3>
                        <p className="text-sm text-gray-500 font-mono">{templateId}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          extends: {customization.extends}
                        </p>
                      </div>
                      {!readOnly && (
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={() => handleEdit(templateId)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                            title="Modifica"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(templateId)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-3">
                    {/* Root Wrapper */}
                    {customization.rootWrapper && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                          Root Wrapper
                        </p>
                        {customization.rootWrapper.className && (
                          <div className="text-sm">
                            <span className="text-gray-600">Classes:</span>{' '}
                            <code className="text-primary-600 bg-primary-50 px-1 rounded text-xs">
                              {customization.rootWrapper.className}
                            </code>
                          </div>
                        )}
                        {customization.rootWrapper.attributes &&
                          Object.keys(customization.rootWrapper.attributes).length > 0 && (
                            <div className="text-sm mt-1">
                              <span className="text-gray-600">Attributes:</span>{' '}
                              <span className="text-gray-700 text-xs">
                                {Object.keys(customization.rootWrapper.attributes).length} definiti
                              </span>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Slot Wrappers */}
                    {customization.slotWrappers &&
                      Object.keys(customization.slotWrappers).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                            Slot Wrappers ({Object.keys(customization.slotWrappers).length})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(customization.slotWrappers).map((slotId) => (
                              <span
                                key={slotId}
                                className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-mono"
                              >
                                {slotId}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Additions */}
                    {customization.additions &&
                      Object.keys(customization.additions).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                            Additions ({Object.keys(customization.additions).length})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(customization.additions).map((position) => (
                              <span
                                key={position}
                                className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded font-mono"
                              >
                                {position}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* No customizations */}
                    {!customization.rootWrapper &&
                      (!customization.slotWrappers ||
                        Object.keys(customization.slotWrappers).length === 0) &&
                      (!customization.additions ||
                        Object.keys(customization.additions).length === 0) && (
                        <div className="text-sm text-gray-400 italic">
                          Nessuna personalizzazione definita
                        </div>
                      )}

                    {/* Metadata */}
                    {customization.metadata && (
                      <div className="pt-2 border-t border-gray-100 text-xs text-gray-500">
                        {customization.metadata.version && (
                          <span>v{customization.metadata.version}</span>
                        )}
                        {customization.metadata.lastModified && (
                          <span className="ml-2">
                            • Updated{' '}
                            {new Date(customization.metadata.lastModified).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      {isFormOpen && (
        <TemplateCustomizationForm
          projectId={projectId}
          templateId={editingTemplateId}
          existingCustomization={
            editingTemplateId ? customizations[editingTemplateId] : undefined
          }
          availableTemplates={availableTemplates}
          uncustomizedTemplates={uncustomizedTemplates}
          onSave={handleSave}
          onCancel={() => setIsFormOpen(false)}
        />
      )}
    </div>
  );
}
